import { type ItemDetails, LegacySegmentTypes } from '@1debit/analytics';
import { ChevronRight, type IconToken } from '@1debit/icons';
import React, {
	type ButtonHTMLAttributes,
	type CSSProperties,
	forwardRef,
	type HTMLAttributes,
	type ReactNode,
} from 'react';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { type AsChildProps, ClickableAnalyticsSlot } from '../../client/ClickableAnalyticsSlot';
import { ActivityIndicator } from '../ActivityIndicator/ActivityIndicator';
import { getIconByKey } from '../utils/assets';
import { classNames } from '../utils/classNames';

export type ButtonSize = 'fullWidth' | 'standard' | 'small';
export type ButtonVariant =
	| 'primary'
	| 'primaryAlert'
	| 'secondary'
	| 'secondaryShade'
	| 'tertiary'
	| 'tertiaryAlert'
	| 'link'
	| 'actionLink';

export type IconPosition = 'leading' | 'trailing';

export type ButtonWithIconProps =
	| {
			size?: ButtonSize;
			variant?: ButtonVariant;
			iconProps?: never;
	  }
	| {
			size?: Exclude<ButtonSize, 'small'>;
			variant?: Exclude<ButtonVariant, 'link' | 'actionLink'>;
			iconProps?: { icon: IconToken; position: IconPosition };
	  };

type ButtonDefaultProps = {
	children: ReactNode;
	analyticsId: AnalyticsId;
	style?: CSSProperties;
	className?: string;
	loading?: boolean;
	disabled?: boolean;
	itemDetails?: ItemDetails;
	itemValue?: string;
} & ButtonWithIconProps;

export type ButtonElementProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>;

export type ButtonProps = AsChildProps<ButtonElementProps> & ButtonDefaultProps;

const sharedStyles = 'inline-flex items-center justify-center rounded-xl text-label origin-center transition-all';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant = 'primary',
			size = 'standard',
			loading,
			asChild,
			analyticsId,
			itemDetails,
			itemValue,
			iconProps,
			children,
			...rest
		},
		ref,
	) => {
		const isLink = variant === 'link' || variant === 'actionLink';
		const canRenderIconFromProps = size !== 'small' && !isLink && iconProps;

		const buttonSizes: Record<ButtonSize, string> = {
			fullWidth: classNames({ 'w-full py-3 px-8': !isLink }),
			standard: classNames({ 'py-3 px-8': !isLink }),
			small: classNames({ 'py-2 px-3': !isLink }),
		};

		const inNormalState = !rest.disabled && !loading;
		const inDisabledState = rest.disabled && !loading;

		const buttonStyles: Record<ButtonVariant, string> = {
			primary: classNames('text-content-dark bg-bg-action-primary', {
				'hover:bg-bg-action-primary-hover': inNormalState,
				'active:bg-bg-action-primary-hover': inNormalState,
				'bg-bg-action-primary-hover': loading,
				'bg-bg-disabled-primary text-content-disabled-primary': inDisabledState,
			}),
			primaryAlert: classNames('text-content-light bg-util-alert-500', {
				'hover:bg-util-alert-400': inNormalState,
				'active:bg-util-alert-400': inNormalState,
				'bg-util-alert-400 text-content-inverse': loading,
				'bg-bg-disabled-primary text-content-disabled-primary': inDisabledState,
			}),
			secondary: classNames('text-content-primary bg-bg-action-secondary', {
				'hover:bg-bg-action-secondary-hover': inNormalState,
				'active:bg-bg-action-secondary-hover active:text-content-pressed-primary': inNormalState,
				'bg-bg-action-secondary-hover': loading,
				'bg-bg-disabled-primary text-content-disabled-primary': inDisabledState,
			}),
			secondaryShade: classNames('text-content-primary bg-bg-shade', {
				'hover:bg-shade-primary-200': inNormalState,
				'active:bg-shade-primary-200 active:text-content-pressed-primary': inNormalState,
				'bg-shade-primary-200': loading,
				'bg-bg-disabled-primary text-content-disabled-primary': inDisabledState,
			}),
			tertiary: classNames('text-content-action-primary bg-none', {
				'hover:bg-bg-action-secondary-hover': inNormalState,
				'active:bg-bg-action-secondary-hover': inNormalState,
				'text-content-primary': loading,
				'text-content-disabled-primary': inDisabledState,
			}),
			tertiaryAlert: classNames('text-util-alert-500 bg-none', {
				'hover:bg-accent-grey-100': inNormalState,
				'active:bg-accent-grey-100': inNormalState,
				'text-content-primary': loading,
				'text-content-disabled-primary': inDisabledState,
			}),
			link: classNames('underline-offset-[3px] underline w-fit text-content-primary', {
				'hover:text-accent-grey-500': inNormalState,
				'active:bg-shade-primary-100 active:rounded-none active:text-accent-grey-500': inNormalState,
				'text-content-disabled-primary': inDisabledState,
			}),
			actionLink: classNames('text-content-primary', {
				'hover:text-accent-grey-500': inNormalState,
				'active:text-accent-grey-500 active:opacity-60': inNormalState,
				'text-content-disabled-primary': inDisabledState,
			}),
		};

		const withIconStyles = classNames('gap-2', {
			'flex-row': iconProps?.position === 'leading',
			'flex-row-reverse': iconProps?.position === 'trailing',
		});

		const styles = classNames(
			sharedStyles,
			buttonStyles[variant],
			buttonSizes[size],
			{
				'cursor-not-allowed': inDisabledState,
				'active:scale-95 cursor-pointer': inNormalState,
				'opacity-0': loading,
				[withIconStyles]: canRenderIconFromProps,
			},
			className,
		);

		const Icon = canRenderIconFromProps ? getIconByKey(iconProps.icon) : null;

		const getButtonContent = () => {
			if (!Icon) {
				if (variant !== 'actionLink') {
					return children;
				}

				if (!asChild) {
					return (
						<>
							{children}
							<ChevronRight data-testid="button-chevron-right" size={16} />
						</>
					);
				}

				// For asChild we have to clone the element and add the icon to the children
				if (React.isValidElement<HTMLAttributes<HTMLElement>>(children)) {
					return React.cloneElement(
						children,
						{
							...children.props,
							className: children.props.className,
						},
						<>
							{children.props.children}
							<ChevronRight data-testid="button-chevron-right" size={16} />
						</>,
					);
				}

				return children;
			}

			if (!asChild) {
				return (
					<>
						<Icon data-testid="button-icon" />
						{children}
					</>
				);
			}

			// For asChild we have to clone the element and add the icon to the children
			if (React.isValidElement<HTMLAttributes<HTMLElement>>(children)) {
				return React.cloneElement(
					children,
					{
						...children.props,
						className: classNames(children.props.className, withIconStyles),
					},
					<>
						<Icon data-testid="button-icon" />
						{children.props.children}
					</>,
				);
			}

			return children;
		};

		const button = (
			<ClickableAnalyticsSlot
				ref={ref}
				className={styles}
				asChild={asChild}
				disabled={rest.disabled || loading}
				analyticsId={analyticsId}
				defaultElement="button"
				trackElementType={LegacySegmentTypes.ElementType.ELEMENT_TYPE_BUTTON}
				itemDetails={itemDetails}
				itemValue={itemValue}
				{...rest}>
				{getButtonContent()}
			</ClickableAnalyticsSlot>
		);

		const loadingWrapperStyles = classNames(styles, 'relative opacity-100 p-0');

		if (loading)
			return (
				<div className={loadingWrapperStyles}>
					<div className="absolute inset-0 flex justify-center items-center">
						<ActivityIndicator className="text-inherit" />
					</div>
					{button}
				</div>
			);

		return button;
	},
);
